import React, { useEffect, useRef, useState } from 'react';
import { Award, Code, TrendingUp } from 'lucide-react';

const Skills = ({ data }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="expertise" ref={sectionRef} className="py-24 md:py-32 bg-[#0f1d32] relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-[#d4af37]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#d4af37]/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className={`mb-16 text-center ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}>
          <span className="text-[#d4af37] text-sm font-semibold uppercase tracking-[0.2em]">Expertise</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4">
            Skills & <span className="text-[#d4af37]">Competencies</span>
          </h2>
          <div className="w-20 h-1 bg-[#d4af37] mt-6 mx-auto" />
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Core Competencies */}
          <div className={`${isVisible ? 'animate-fade-up delay-200' : 'opacity-0'}`}>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-[#d4af37]/10 flex items-center justify-center">
                <Award className="text-[#d4af37]" size={24} />
              </div>
              <h3 className="text-2xl font-bold text-white">Core Competencies</h3>
            </div>

            <div className="space-y-4">
              {data.core.map((skill, index) => (
                <div
                  key={index}
                  className="group flex items-center justify-between p-4 bg-[#1a2744] border border-[#d4af37]/10 hover:border-[#d4af37]/40 transition-all duration-300"
                >
                  <div className="flex items-center gap-3">
                    <TrendingUp size={16} className="text-[#d4af37]" />
                    <span className="text-white font-medium group-hover:text-[#d4af37] transition-colors">
                      {skill.name}
                    </span>
                  </div>
                  {skill.endorsements && (
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-24 bg-[#0a1628] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#d4af37] to-[#e8c547] rounded-full transition-all duration-1000"
                          style={{ width: `${Math.min(skill.endorsements * 8, 100)}%` }}
                        />
                      </div>
                      <span className="text-[#d4af37] text-sm font-bold">{skill.endorsements}+</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Technical Skills */}
          <div className={`${isVisible ? 'animate-fade-up delay-400' : 'opacity-0'}`}>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-[#d4af37]/10 flex items-center justify-center">
                <Code className="text-[#d4af37]" size={24} />
              </div>
              <h3 className="text-2xl font-bold text-white">Technical Skills</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {data.technical.map((skill, index) => (
                <div
                  key={index}
                  className="group p-5 bg-[#1a2744] border border-[#d4af37]/10 hover:border-[#d4af37]/40 hover:bg-[#d4af37]/5 transition-all duration-300 text-center"
                >
                  <span className="text-[#94a3b8] group-hover:text-white font-medium transition-colors">
                    {skill}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
